import type { Currency } from './currency.js';

export const AccountType = {
  Inquirer: 'inquirer',
  Consultant: 'consultant',
  SolutionProvider: 'solutionProvider',
};

export const AccountTypeValues = Object.values(AccountType);

export type AccountType = (typeof AccountType)[keyof typeof AccountType];

export const Language = {
  CS: 'cs',
  EN: 'en',
} as const;

export const LanguageValues = Object.values(Language);

export type LanguageValue = (typeof Language)[keyof typeof Language];

/** Inquirer account company size band (stored on Account). */
export const CompanySize = {
  Band1_10: '1_10',
  Band11_50: '11_50',
  Band51_200: '51_200',
  Band201_1000: '201_1000',
  Band1000Plus: '1000_plus',
} as const;

export const CompanySizeValues = Object.values(CompanySize);

export type CompanySize = (typeof CompanySize)[keyof typeof CompanySize];

/** Admin-set recognition on an account (nullable in DB). */
export const HonoraryFlag = {
  FoundingDesignPartner: 'foundingDesignPartner',
} as const;

export const HonoraryFlagValues = Object.values(HonoraryFlag);

export type HonoraryFlagValue = (typeof HonoraryFlag)[keyof typeof HonoraryFlag];

/** How the account was created; null for regular self-service/admin accounts. */
export const AccountSignupSource = {
  Exhibition: 'exhibition',
  LinkedIn: 'linkedin',
  Google: 'google',
} as const;

export const AccountSignupSourceValues = Object.values(AccountSignupSource);

export type AccountSignupSource =
  (typeof AccountSignupSource)[keyof typeof AccountSignupSource];

/** Admin approval gate for new self-service registrations. */
export const AccountRegistrationStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
} as const;

export const AccountRegistrationStatusValues = Object.values(AccountRegistrationStatus);

export type AccountRegistrationStatus =
  (typeof AccountRegistrationStatus)[keyof typeof AccountRegistrationStatus];

/** Max length for optional public display name (brand name). */
export const ACCOUNT_DISPLAY_NAME_MAX_LENGTH = 255;

export interface AccountDto {
  id: string;
  accountType: AccountType;
  /** Optional brand name for public/published contexts; falls back to legal company name. */
  displayName?: string | null;
  defaultCurrency?: Currency;
  language: LanguageValue;
  /** IANA time zone id, e.g. Europe/Prague; set during supplier onboarding (account owner). */
  timeZone?: string | null;
  /** Primary industry (codebook_industry.id); inquirer accounts only. */
  companyIndustryId?: string | null;
  companySize?: CompanySize | null;
  /** ISO timestamp when admin verified the inquirer company (brief publish gate). */
  companyVerifiedAt?: string | null;
  honoraryFlag?: HonoraryFlagValue | null;
  /** Superadmin-set opt-in for PRINCE2 governance, approvals, and the portfolio dashboard. */
  prince2Enabled?: boolean;
  /** Superadmin-set exclusive AWS Bedrock (EU) routing for all LLM and embedding traffic. */
  bedrockEnabled?: boolean;
  /** Parent account of a one-level group (corporate level). */
  parentAccountId?: string | null;
  /** When true, governance escalations of this account go to the parent account. */
  escalateToParent?: boolean;
  /** Self-service registration approval gate. */
  registrationStatus?: AccountRegistrationStatus;
  /** ISO timestamp when admin approved the account registration. */
  approvedAt?: string | null;
  /** ISO timestamp when admin rejected the account registration. */
  rejectedAt?: string | null;
  /** ISO timestamp when the user agreed to Terms of Service and Privacy Policy (at signup). */
  termsAcceptedAt?: string | null;
  /** How the account was created (e.g. exhibition instant signup); null otherwise. */
  signupSource?: AccountSignupSource | null;
}

export interface SetAccountParentDto {
  /** Null removes the account from its group. */
  parentAccountId: string | null;
  escalateToParent?: boolean;
}
